<template>
  <div class="admin-notification-batch-detail">
    <div class="batch-page-header">
      <div class="batch-page-header__copy">
        <div class="batch-page-header__title-row">
          <v-btn
            icon
            small
            class="mr-2"
            :to="{ name: 'admin.notification-batches.list' }"
            title="Listeye don"
          >
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <h1>Gönderi #{{ batch ? batch.id : id }}</h1>
        </div>
        <p>
          Toplu bildirimin hedefi, teslimat durumu ve alıcı kayıtları tek
          ekrandan izlenir.
        </p>
        <div v-if="batch" class="batch-context-chips">
          <span class="batch-context-chip">
            Kanal:
            <strong>{{ batch.notification_channel }}</strong>
          </span>
          <span class="batch-context-chip">
            Servis:
            <strong>{{ formatProvider(batch.delivery_provider) }}</strong>
          </span>
          <span class="batch-context-chip">
            Oluşturan:
            <strong>{{ batch.created_by_name || "-" }}</strong>
          </span>
          <span class="batch-context-chip">
            Tarih:
            <strong>
              <rs-table-cell-date show-time :value="batch.created_at" />
            </strong>
          </span>
        </div>
      </div>

      <div class="batch-page-header__actions">
        <v-btn
          text
          small
          :disabled="!batch || !failedCount || isRetrying"
          :loading="isRetrying"
          @click="retryFailed"
        >
          <v-icon left small>mdi-restart</v-icon>
          Hatalıları Tekrar Dene
        </v-btn>
        <v-btn
          outlined
          color="primary"
          small
          @click="refreshAll"
          :loading="isBatchLoading || isLoading"
        >
          <v-icon left small>mdi-refresh</v-icon>
          Yenile
        </v-btn>
      </div>
    </div>

    <v-card v-if="batch" class="mb-4 batch-progress-card">
      <v-card-text>
        <div class="batch-progress-card__top">
          <div>
            <div class="batch-progress-card__title">Servise İletiliyor</div>
            <div class="batch-progress-card__subtitle">
              Hedeflenen alıcıların servise aktarımı
            </div>
          </div>
          <div class="batch-progress-card__count">
            <strong>{{ serviceProgressCount }}</strong>
            <span>/ {{ serviceProgressTarget }}</span>
            <em>%{{ serviceProgressPercent }}</em>
          </div>
        </div>
        <v-progress-linear
          class="batch-progress-card__bar"
          :value="serviceProgressPercent"
          height="8"
          rounded
          color="primary"
          background-color="#e3e6f0"
        />
      </v-card-text>
    </v-card>

    <v-card class="mb-4 batch-summary-panel">
      <v-card-text v-if="batch" class="pa-0">
        <div class="batch-section-header">
          <div>
            <div class="batch-section-header__title">Gönderi Özeti</div>
            <div class="batch-section-header__subtitle">
              Teslimat durumu alıcı bazında güncel gönderim kayıtlarından takip
              edilir.
            </div>
          </div>
          <div class="batch-delivery-chip" :class="deliveryToneClass">
            <v-icon small>{{ deliveryToneIcon }}</v-icon>
            <span>{{ deliveryToneText }}</span>
          </div>
        </div>

        <div class="batch-summary-body">
          <div class="batch-message-card">
            <div class="batch-message-card__eyebrow">Mesaj</div>
            <h2>{{ batch.subject || "Başlık yok" }}</h2>
            <p>{{ batch.body || "Mesaj içeriği yok." }}</p>
          </div>

          <div class="batch-stat-grid">
            <div
              v-for="item in summaryStats"
              :key="item.key"
              class="batch-stat"
              :class="item.class"
            >
              <span>{{ item.label }}</span>
              <strong>{{ item.value }}</strong>
              <small>{{ item.caption }}</small>
            </div>
          </div>
        </div>
      </v-card-text>
      <v-card-text v-else class="text--secondary">
        Batch bilgisi yukleniyor...
      </v-card-text>
    </v-card>

    <v-card class="batch-recipients-panel">
      <div class="batch-section-header batch-section-header--table">
        <div>
          <div class="batch-section-header__title">Alıcı Detayı</div>
          <div class="batch-section-header__subtitle">
            Kullanıcı bazında teslimat durumu, deneme sayısı ve hata sebebi.
          </div>
        </div>
      </div>
      <v-card-text class="pt-0">
        <v-data-table
          v-bind="dataTableAttrs"
          :headers="headersShown"
          :items="list"
          :loading="isLoading"
          :options.sync="options"
          :server-items-length="total"
          :items-per-page.sync="itemsPerPage"
          v-model="selectedItems"
          @update:items-per-page="handleItemsPerPageUpdated"
          :show-select="false"
        >
          <template v-slot:top>
            <rs-table-title-bar
              v-if="$refs.headSearch"
              title="Kullanıcılar"
              icon="mdi-account-group"
              @clear-filters="$refs.headSearch.reset"
              @reload="loadList"
              hide-edit
              :search.sync="search.query"
              v-bind="titleBarAttrs"
            ></rs-table-title-bar>
          </template>

          <!-- eslint-disable-next-line -->
          <template v-slot:body.prepend="{ headers }">
            <rs-table-head-search
              :headers="headers"
              :search.sync="search"
              ref="headSearch"
            />
          </template>

          <!-- eslint-disable-next-line -->
          <template v-slot:item.email="{ value }">
            <rs-table-cell-email v-if="value" :value="value" />
            <span v-else>Yok</span>
          </template>

          <!-- eslint-disable-next-line -->
          <template v-slot:item.phone="{ value }">
            <rs-table-cell-phone :value="value" />
          </template>

          <!-- eslint-disable-next-line -->
          <template v-slot:item.delivery_state="{ item }">
            <v-tooltip
              v-if="formatRecipientFailure(item) !== '-'"
              bottom
              max-width="320"
            >
              <template v-slot:activator="{ on, attrs }">
                <span
                  class="delivery-state-chip"
                  :class="deliveryStateChipClass(item.delivery_state)"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon small>{{
                    deliveryStateIcon(item.delivery_state)
                  }}</v-icon>
                  {{ deliveryStateText(item.delivery_state) }}
                </span>
              </template>
              <span>{{ formatRecipientFailure(item) }}</span>
            </v-tooltip>
            <span
              v-else
              class="delivery-state-chip"
              :class="deliveryStateChipClass(item.delivery_state)"
            >
              <v-icon small>{{
                deliveryStateIcon(item.delivery_state)
              }}</v-icon>
              {{ deliveryStateText(item.delivery_state) }}
            </span>
          </template>

          <!-- eslint-disable-next-line -->
          <template v-slot:item.delivery_attempt_count="{ value }">
            <span>{{ value || 0 }} Kez</span>
          </template>

          <!-- eslint-disable-next-line -->
          <template v-slot:item.delivery_timeline="{ item }">
            <v-tooltip bottom max-width="340">
              <template v-slot:activator="{ on, attrs }">
                <span class="delivery-timeline" v-bind="attrs" v-on="on">
                  <strong>{{ primaryTimelineLabel(item) }}</strong>
                  <span>{{ primaryTimelineDate(item) }}</span>
                </span>
              </template>
              <div class="delivery-timeline-tooltip">
                <div
                  v-for="entry in deliveryTimelineEntries(item)"
                  :key="entry.key"
                >
                  <strong>{{ entry.label }}</strong>
                  <span>{{ entry.value }}</span>
                </div>
              </div>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { debounce } from "@/core/plugins/debounce";
import { filtersToURL } from "@/view/mixins";

export default {
  name: "AdminNotificationBatchDetail",
  props: {
    id: {
      type: Number,
      required: true,
    },
  },
  mixins: [filtersToURL],
  computed: {
    hasDeliveryStats() {
      return Number(this.batch?.delivery_state_count || 0) > 0;
    },
    pendingCount() {
      if (!this.batch) {
        return 0;
      }

      return this.hasDeliveryStats
        ? this.batch.delivery_pending_count || 0
        : this.batch.pending_count || 0;
    },
    failedCount() {
      if (!this.batch) {
        return 0;
      }

      return this.hasDeliveryStats
        ? this.batch.delivery_failed_count || 0
        : this.batch.error_count || 0;
    },
    deliveredCount() {
      if (!this.batch) {
        return 0;
      }

      return this.hasDeliveryStats
        ? this.batch.delivery_delivered_count || 0
        : this.batch.delivered_count || 0;
    },
    serviceProgressTarget() {
      return Number(this.batch?.target_count || 0);
    },
    serviceProgressCount() {
      return (
        Number(this.batch?.provider_accepted_count || 0) + this.deliveredCount
      );
    },
    serviceProgressPercent() {
      if (!this.serviceProgressTarget) {
        return 0;
      }

      return Math.min(
        100,
        Math.round(
          (this.serviceProgressCount / this.serviceProgressTarget) * 100
        )
      );
    },
    shouldPollBatch() {
      if (!this.batch || !this.serviceProgressTarget) {
        return false;
      }

      return this.serviceProgressCount < this.serviceProgressTarget;
    },
    deliveryToneClass() {
      if (!this.batch) {
        return "batch-delivery-chip--pending";
      }

      if (Number(this.batch.needs_attention_count || 0) > 0) {
        return "batch-delivery-chip--warning";
      }

      if (this.failedCount > 0) {
        return "batch-delivery-chip--danger";
      }

      if (this.pendingCount > 0) {
        return "batch-delivery-chip--pending";
      }

      return "batch-delivery-chip--success";
    },
    deliveryToneIcon() {
      const map = {
        "batch-delivery-chip--success": "mdi-check-circle-outline",
        "batch-delivery-chip--warning": "mdi-alert-circle-outline",
        "batch-delivery-chip--danger": "mdi-alert-octagon-outline",
        "batch-delivery-chip--pending": "mdi-progress-clock",
      };

      return map[this.deliveryToneClass] || "mdi-progress-clock";
    },
    deliveryToneText() {
      if (!this.batch) {
        return "Yükleniyor";
      }

      if (Number(this.batch.needs_attention_count || 0) > 0) {
        return "Devam Ediyor";
      }

      if (this.failedCount > 0) {
        return "Hatalar Var";
      }

      if (this.pendingCount > 0) {
        return "Akış devam ediyor";
      }

      return "Gönderi Tamamlandı";
    },
    summaryStats() {
      if (!this.batch) {
        return [];
      }

      return [
        {
          key: "target",
          label: "Hedef",
          value: this.batch.target_count || 0,
          caption: "Beklenen alıcı",
          class: "batch-stat--neutral",
        },
        {
          key: "pending",
          label: "Bekleyen",
          value: this.pendingCount,
          caption: "Kuyruk veya hazırlık",
          class: "batch-stat--pending",
        },
        {
          key: "accepted",
          label: "Servise İletilen",
          value: this.batch.provider_accepted_count || 0,
          caption: "Servis tarafına aktarılan",
          class: "batch-stat--accepted",
        },
        {
          key: "delivered",
          label: "Alıcıya İletilen",
          value: this.deliveredCount,
          caption: "Teslimi doğrulanan",
          class: "batch-stat--delivered",
        },
        {
          key: "failed",
          label: "Başarısız",
          value: this.failedCount,
          caption: "Kalıcı veya servis hatası",
          class: "batch-stat--failed",
        },
        {
          key: "attention",
          label: "Hata",
          value: this.batch.needs_attention_count || 0,
          caption: "Manuel kontrol gereken",
          class: "batch-stat--attention",
        },
      ];
    },
  },
  data() {
    return {
      batch: null,
      isBatchLoading: false,
      isRetrying: false,
      batchPollTimer: null,
      batchPollStartedAt: null,
      batchPollMaxDurationMs: 10 * 60 * 1000,
      options: {
        sortBy: ["user_name"],
        sortDesc: [false],
      },
      dataTableAttrs: {
        footerProps: {
          disableItemsPerPage: false,
        },
      },
      search: {
        query: null,
        delivery_bucket: null,
      },
      deliveryBucketList: [
        { id: "pending", name: "Bekleyen" },
        { id: "accepted", name: "Servise İletilen" },
        { id: "delivered", name: "Alıcıya İletilen" },
        { id: "failed", name: "Başarısız" },
        { id: "attention", name: "Hata" },
        { id: "invalid", name: "Eksik" },
      ],
      deliveryStateList: [
        { id: "pending", name: "Bekleyen" },
        { id: "queued", name: "Bekleyen" },
        { id: "sending", name: "Bekleyen" },
        { id: "provider_accepted", name: "Servise İletilen" },
        { id: "delivered", name: "Alıcıya İletilen" },
        { id: "retry_scheduled", name: "Bekleyen" },
        { id: "ambiguous_timeout", name: "Hata" },
        { id: "provider_failed", name: "Başarısız" },
        { id: "skipped_invalid_recipient", name: "Eksik" },
        { id: "failed_permanent", name: "Başarısız" },
      ],
      headers: [
        {
          text: "Ad",
          value: "user_name",
          searchable: "text",
          width: "200px",
        },
        {
          text: "E-posta",
          value: "email",
          searchable: "text",
          width: "220px",
        },
        {
          text: "Telefon",
          value: "phone",
          searchable: "text",
          width: "160px",
        },
        {
          text: "Teslimat",
          value: "delivery_state",
          searchable: "select",
          options: () => this.deliveryBucketList,
          itemText: "name",
          itemValue: "id",
          searchValue: "delivery_bucket",
          width: "170px",
        },
        {
          text: "Deneme",
          value: "delivery_attempt_count",
          searchable: false,
          align: "end",
          width: "80px",
          notPrice: true,
        },
        {
          text: "Zaman",
          value: "delivery_timeline",
          searchable: false,
          sortable: false,
          width: "160px",
        },
      ],
      titleBarAttrs: {},
    };
  },
  watch: {
    options: {
      handler() {
        this.setURLParams();
      },
      deep: true,
    },
    search: {
      handler: debounce(function () {
        this.options.page = 1;
        this.setURLParams();
      }, 250),
      deep: true,
    },
    id() {
      this.stopBatchPolling();
      this.refreshAll();
    },
  },
  mounted() {
    this.refreshAll();
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
  },
  beforeDestroy() {
    this.stopBatchPolling();
    document.removeEventListener(
      "visibilitychange",
      this.handleVisibilityChange
    );
  },
  methods: {
    formatProvider(value) {
      if (!value) {
        return "-";
      }

      const map = {
        goldsms: "GoldSMS",
        masgsm: "MasGSM",
        mailer: "Laravel Mailer",
        onesignal: "OneSignal",
      };

      return map[value] || value;
    },
    deliveryStateText(value) {
      if (!value) {
        return "Oluşturuldu";
      }

      const item = this.deliveryStateList.find((state) => state.id === value);

      return item ? item.name : value;
    },
    deliveryStateChipClass(value) {
      const map = {
        pending: "delivery-state-chip--pending",
        queued: "delivery-state-chip--pending",
        sending: "delivery-state-chip--pending",
        provider_accepted: "delivery-state-chip--accepted",
        delivered: "delivery-state-chip--delivered",
        retry_scheduled: "delivery-state-chip--pending",
        ambiguous_timeout: "delivery-state-chip--attention",
        provider_failed: "delivery-state-chip--failed",
        skipped_invalid_recipient: "delivery-state-chip--invalid",
        failed_permanent: "delivery-state-chip--failed",
      };

      return map[value] || "delivery-state-chip--empty";
    },
    deliveryStateIcon(value) {
      const map = {
        pending: "mdi-clock-outline",
        queued: "mdi-clock-outline",
        sending: "mdi-clock-outline",
        provider_accepted: "mdi-check",
        delivered: "mdi-check-all",
        retry_scheduled: "mdi-clock-outline",
        ambiguous_timeout: "mdi-alert-circle-outline",
        provider_failed: "mdi-alert-circle-outline",
        skipped_invalid_recipient: "mdi-account-alert-outline",
        failed_permanent: "mdi-alert-circle-outline",
      };

      return map[value] || "mdi-help-circle-outline";
    },
    formatErrorReason(value) {
      if (!value) {
        return "-";
      }

      const map = {
        user_not_found: "Kullanıcı bulunamadı",
        missing_email: "E-posta yok",
        invalid_email: "E-posta gecersiz",
        unsupported_email_characters: "E-posta karakteri desteklenmiyor",
        missing_phone: "Telefon yok",
        missing_mobile_token: "Mobil token yok",
        notify_exception: "Gönderim hatası",
        provider_error: "Servis hatası",
      };

      return map[value] || value;
    },
    formatRecipientFailure(item) {
      const reason =
        item.failure_reason ||
        item.provider_failure_reason ||
        item.last_error_message ||
        item.error_reason;

      if (!reason) {
        return "-";
      }

      return this.formatErrorReason(reason);
    },
    formatTimelineDate(value) {
      if (!value) {
        return "Yok";
      }

      return new Date(value).toLocaleString();
    },
    deliveryTimelineEntries(item) {
      return [
        {
          key: "queued",
          label: "Bekleyen",
          value: this.formatTimelineDate(item.queued_at),
        },
        {
          key: "accepted",
          label: "Servise İletilen",
          value: this.formatTimelineDate(item.accepted_at || item.sent_at),
        },
        {
          key: "delivered",
          label: "Alıcıya İletilen",
          value: this.formatTimelineDate(item.delivered_at),
        },
        {
          key: "last_attempt",
          label: "Son Deneme",
          value: this.formatTimelineDate(item.last_attempt_at),
        },
        {
          key: "failed",
          label: "Hata",
          value: this.formatTimelineDate(item.failed_at),
        },
      ];
    },
    primaryTimelineEntry(item) {
      const entries = this.deliveryTimelineEntries(item);
      const filled = entries.filter((entry) => entry.value !== "Yok");

      return filled[filled.length - 1] || entries[0];
    },
    primaryTimelineLabel(item) {
      return this.primaryTimelineEntry(item).label;
    },
    primaryTimelineDate(item) {
      return this.primaryTimelineEntry(item).value;
    },
    refreshAll() {
      this.loadBatch();
      this.loadList();
    },
    startBatchPolling() {
      if (this.batchPollTimer || !this.shouldPollBatch || document.hidden) {
        return;
      }

      this.batchPollStartedAt = Date.now();
      this.scheduleNextBatchPoll();
    },
    stopBatchPolling() {
      if (this.batchPollTimer) {
        clearTimeout(this.batchPollTimer);
        this.batchPollTimer = null;
      }

      this.batchPollStartedAt = null;
    },
    getBatchPollDelay() {
      const elapsed = Date.now() - (this.batchPollStartedAt || Date.now());

      if (elapsed < 30 * 1000) {
        return 5 * 1000;
      }

      if (elapsed < 2 * 60 * 1000) {
        return 10 * 1000;
      }

      return 30 * 1000;
    },
    scheduleNextBatchPoll() {
      if (!this.shouldPollBatch || document.hidden) {
        this.stopBatchPolling();
        return;
      }

      const elapsed = Date.now() - (this.batchPollStartedAt || Date.now());

      if (elapsed >= this.batchPollMaxDurationMs) {
        this.stopBatchPolling();
        return;
      }

      this.batchPollTimer = setTimeout(() => {
        this.batchPollTimer = null;
        this.loadBatch(true);
      }, this.getBatchPollDelay());
    },
    handleVisibilityChange() {
      if (document.hidden) {
        this.stopBatchPolling();
        return;
      }

      if (this.shouldPollBatch) {
        this.loadBatch(true);
      }
    },
    retryFailed() {
      if (this.isRetrying) {
        return;
      }

      this.isRetrying = true;

      this.$api
        .post(
          `api-super-admin/admin-notification-batches/${this.id}/retry-failed`
        )
        .then((response) => {
          const failedCount = response.data.data?.failed_count ?? 0;
          if (!failedCount) {
            this.$toast.success("Tekrar denenecek hata bulunamadı.");
          } else {
            this.$toast.success(
              `${failedCount} kullanıcı için tekrar deneme başlatıldı.`
            );
          }
          this.refreshAll();
        })
        .catch((error) => {
          this.handleError(error);
        })
        .finally(() => {
          this.isRetrying = false;
        });
    },
    loadBatch(fromPoll = false) {
      if (this.isBatchLoading) {
        return;
      }

      this.isBatchLoading = true;

      this.$api
        .query(`api-super-admin/admin-notification-batches/${this.id}`)
        .then((response) => {
          this.batch = response.data.data || response.data;
        })
        .catch((error) => {
          this.handleError(error);
        })
        .finally(() => {
          this.isBatchLoading = false;
          if (fromPoll) {
            this.scheduleNextBatchPoll();
          } else if (this.shouldPollBatch) {
            this.startBatchPolling();
          } else {
            this.stopBatchPolling();
          }
        });
    },
    getParams() {
      return this.buildSearchParams(this.search, this.options);
    },
    loadList() {
      if (this.isLoading) {
        return;
      }

      this.isLoading = true;
      this.list = [];
      this.footTotals = [];

      const params = this.getParams();

      this.$api
        .query(
          `api-super-admin/admin-notification-batches/${this.id}/recipients`,
          {
            params,
          }
        )
        .then((response) => {
          this.loadListFromResponse(response);
        })
        .catch((error) => {
          this.handleError(error);
        })
        .finally(() => (this.isLoading = false));
    },
  },
};
</script>

<style scoped>
.admin-notification-batch-detail {
  background: #f0f1fa;
  margin: -12px;
  padding: 24px;
}

.batch-page-header {
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 18px;
}

.batch-page-header__copy {
  min-width: 0;
}

.batch-page-header__title-row {
  align-items: center;
  display: flex;
  margin-bottom: 4px;
}

.batch-page-header h1 {
  color: #2b2d35;
  font-size: 20px;
  font-weight: 800;
  line-height: 1.25;
  margin: 0;
}

.batch-page-header p {
  color: #8b8e9c;
  font-size: 13px;
  font-weight: 500;
  margin: 0;
}

.batch-page-header__actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.batch-context-chips {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.batch-context-chip {
  background: #fff;
  border: 1px solid #e3e6f0;
  border-radius: 999px;
  color: #7c7f8c;
  display: inline-flex;
  font-size: 12px;
  font-weight: 700;
  gap: 4px;
  line-height: 1.2;
  padding: 7px 12px;
}

.batch-context-chip strong {
  color: #2b2d35;
  font-weight: 800;
}

.batch-summary-panel,
.batch-progress-card,
.batch-recipients-panel {
  border: 1px solid #d9dbea;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(32, 33, 45, 0.1);
  overflow: hidden;
}

.batch-progress-card >>> .v-card__text {
  padding: 14px 18px;
}

.batch-progress-card__top {
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 10px;
}

.batch-progress-card__title {
  color: #2b2d35;
  font-size: 14px;
  font-weight: 800;
  line-height: 1.2;
  margin-bottom: 3px;
}

.batch-progress-card__subtitle {
  color: #7c7f8c;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.3;
}

.batch-progress-card__count {
  align-items: baseline;
  color: #7c7f8c;
  display: flex;
  flex: 0 0 auto;
  font-size: 13px;
  font-weight: 700;
  gap: 3px;
}

.batch-progress-card__count strong {
  color: #2b2d35;
  font-size: 18px;
  font-weight: 800;
}

.batch-progress-card__count em {
  color: #5867dd;
  font-style: normal;
  margin-left: 8px;
}

.batch-progress-card__bar {
  border-radius: 999px;
}

.batch-section-header {
  align-items: center;
  border-bottom: 1px solid #dedfeb;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  padding: 22px 26px;
}

.batch-section-header--table {
  border-bottom: 0;
  padding-bottom: 12px;
}

.batch-section-header__title {
  color: #2b2d35;
  font-size: 20px;
  font-weight: 800;
  line-height: 1.25;
  margin-bottom: 6px;
}

.batch-section-header__subtitle {
  color: #7c7f8c;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.35;
}

.batch-delivery-chip {
  align-items: center;
  border-radius: 4px;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 13px;
  font-weight: 800;
  gap: 8px;
  padding: 9px 11px;
}

.batch-delivery-chip--success {
  background: #e8fff3;
  color: #1bc5bd;
}

.batch-delivery-chip--warning,
.batch-delivery-chip--pending {
  background: #fff8dd;
  color: #ffa800;
}

.batch-delivery-chip--danger {
  background: #fff4f4;
  color: #f64e60;
}

.batch-summary-body {
  display: grid;
  gap: 16px;
  grid-template-columns: minmax(280px, 0.8fr) minmax(0, 1.2fr);
  padding: 20px 26px 26px;
}

.batch-message-card {
  background: #fafafe;
  border: 1px dashed #d9dbea;
  border-radius: 4px;
  min-height: 154px;
  padding: 16px;
}

.batch-message-card__eyebrow {
  color: #7c7f8c;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.04em;
  margin-bottom: 8px;
  text-transform: uppercase;
}

.batch-message-card h2 {
  color: #2b2d35;
  font-size: 18px;
  font-weight: 800;
  line-height: 1.25;
  margin: 0 0 10px;
  overflow-wrap: anywhere;
}

.batch-message-card p {
  color: #5d6374;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.45;
  margin: 0;
  overflow-wrap: anywhere;
}

.batch-stat-grid {
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.batch-stat {
  background: #fff;
  border: 1px solid #e3e6f0;
  border-left: 4px solid #b5b5c3;
  border-radius: 4px;
  min-height: 112px;
  padding: 14px;
}

.batch-stat span {
  color: #74788d;
  display: block;
  font-size: 12px;
  font-weight: 600;
}

.batch-stat strong {
  color: #2b2d3a;
  display: block;
  font-size: 30px;
  font-weight: 800;
  line-height: 1.25;
  margin-top: 6px;
}

.batch-stat small {
  color: #7c7f8c;
  display: block;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.3;
  margin-top: 4px;
}

.batch-stat--pending {
  border-left-color: #90a4ae;
}

.batch-stat--accepted {
  border-left-color: #5867dd;
}

.batch-stat--delivered {
  border-left-color: #1bc5bd;
}

.batch-stat--failed {
  border-left-color: #f64e60;
}

.batch-stat--skipped,
.batch-stat--attention {
  border-left-color: #ffa800;
}

.delivery-state-chip {
  align-items: center;
  background: transparent;
  border: 1px solid currentColor;
  border-radius: 999px;
  display: inline-flex;
  gap: 7px;
  font-size: 13px;
  font-weight: 600;
  justify-content: center;
  line-height: 1;
  min-width: 124px;
  padding: 7px 14px;
  white-space: nowrap;
}

.delivery-state-chip .v-icon {
  color: currentColor;
}

.delivery-state-chip--pending {
  color: #ffa800;
}

.delivery-state-chip--accepted {
  color: #6f42c1;
}

.delivery-state-chip--delivered {
  color: #00b894;
}

.delivery-state-chip--failed {
  color: #ff2f6d;
}

.delivery-state-chip--attention {
  color: #ff2f6d;
}

.delivery-state-chip--invalid {
  color: #ffa800;
}

.delivery-state-chip--empty {
  color: #b5b5c3;
}

.delivery-timeline {
  color: #555b6d;
  cursor: default;
  display: inline-flex;
  flex-direction: column;
  font-size: 12px;
  line-height: 1.35;
}

.delivery-timeline strong {
  color: #2b2d35;
  font-size: 12px;
  font-weight: 700;
}

.delivery-timeline span {
  color: #74788d;
}

.delivery-timeline-tooltip {
  display: grid;
  gap: 6px;
}

.delivery-timeline-tooltip div {
  display: grid;
  gap: 2px;
}

.delivery-timeline-tooltip strong {
  font-size: 12px;
  font-weight: 700;
}

.delivery-timeline-tooltip span {
  font-size: 12px;
}

.batch-recipients-panel >>> .v-data-table tbody td {
  padding-top: 12px;
  padding-bottom: 12px;
}

@media (max-width: 1264px) {
  .batch-summary-body {
    grid-template-columns: 1fr;
  }

  .batch-stat-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 600px) {
  .admin-notification-batch-detail {
    margin: -8px;
    padding: 14px;
  }

  .batch-page-header,
  .batch-section-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .batch-page-header__actions {
    justify-content: flex-start;
  }

  .batch-summary-body,
  .batch-section-header {
    padding-left: 16px;
    padding-right: 16px;
  }

  .batch-stat-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
